import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { ShareButtons } from '../components/ShareButtons';
import { 
  Music, Video, Plus, Zap, User, CreditCard, 
  Play, Pause, Trash2, Clock, CheckCircle, AlertCircle, Loader2, Image, Gift, AlertTriangle, Lock, Unlock, Download, Eye, Share2, Headphones, X, ChevronLeft, ChevronRight,
  Star, Archive, Search, Bookmark, ListMusic, CheckSquare, Square, Tag
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import MainNav from '../components/MainNav';
import { CoachEye } from '../components/Coach';
import AddToPlaylistDialog from '../components/AddToPlaylistDialog';
import CategoriesFilterPopover from '../components/CategoriesFilterPopover';
import ManageCategoriesModal from '../components/ManageCategoriesModal';
import CategorizeSongPopover from '../components/CategorizeSongPopover';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function Dashboard() {
  const { user, loading: authLoading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [songs, setSongs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('songs');

  // v2.9.52 — Song Organization: filter pills + Mood + Genre dropdowns
  // + search box. Pill choice + mood/genre persist to localStorage so
  // heavy users don't re-pick on every visit. Search stays session-only.
  const [categoryFilter, setCategoryFilter] = useState(() => {
    try {
      return localStorage.getItem('wim_song_category') || 'all';
    } catch {
      return 'all';
    }
  });
  const [moodFilter, setMoodFilter] = useState(() => {
    try { return localStorage.getItem('wim_song_mood') || ''; } catch { return ''; }
  });
  const [genreFilter, setGenreFilter] = useState(() => {
    try { return localStorage.getItem('wim_song_genre') || ''; } catch { return ''; }
  });
  const [songSearch, setSongSearch] = useState('');
  // Fallback list matches CreateSong defaults exactly so first paint
  // looks identical to what the user picks during creation.
  const [moodOptions, setMoodOptions] = useState(['agressive', 'dark', 'energetic', 'hopeful', 'melancholic', 'nostalgic', 'peaceful', 'romantic', 'upbeat']);
  const [genreOptions, setGenreOptions] = useState(['acoustic', 'classical', 'country', 'country-rock', 'electronic', 'gospel', 'hip-hop', 'indie', 'jazz', 'metal', 'pop', 'r&b', 'rock', 'smooth jazz']);
  const [togglingCategoryId, setTogglingCategoryId] = useState(null);

  // v2.9.55 — Dashboard filter presets (mirror of Library presets).
  // Backend `library_presets` collection scoped with scope="dashboard".
  const [dashPresets, setDashPresets] = useState([]);
  const [dashPresetCap, setDashPresetCap] = useState(10);
  const [savingDashPreset, setSavingDashPreset] = useState(false);
  const [newDashPresetName, setNewDashPresetName] = useState('');
  const [showDashPresetInput, setShowDashPresetInput] = useState(false);
  // v2.9.57 — auto-apply the default preset once on cold load. Ref prevents
  // re-applying if presets are re-fetched later (e.g. after save).
  const dashDefaultAppliedRef = useRef(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [lowCreditsToastShown, setLowCreditsToastShown] = useState(false);
  
  // Delete confirmation state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, title: '' });
  
  // Unlock song state
  const [unlockingSongId, setUnlockingSongId] = useState(null);
  const [unlockCost, setUnlockCost] = useState(20);
  const [songGenerationCost, setSongGenerationCost] = useState(20);

  // Audio player state
  const audioRef = useRef(null);

  // Inline video popup player — lets users play a dashboard video without
  // leaving the page (avoids a costly full reload of /dashboard on return).
  const [popupVideo, setPopupVideo] = useState(null);
  const popupVideoRef = useRef(null);

  const closePopupVideo = useCallback(() => {
    try {
      if (popupVideoRef.current) {
        popupVideoRef.current.pause();
        popupVideoRef.current.currentTime = 0;
      }
    } catch { /* pause() on a detached / not-yet-loaded media element is safe to ignore */ }
    setPopupVideo(null);
  }, []);

  // Esc key + arrow-key navigation for the video popup.
  // ← / → flips to the previous / next completed video in the list.
  useEffect(() => {
    if (!popupVideo) return;
    const onKey = (e) => {
      if (e.key === 'Escape') return closePopupVideo();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const playable = videos.filter(v => v.status === 'completed' && v.video_url);
        if (playable.length < 2) return;
        const idx = playable.findIndex(v => v.video_id === popupVideo.video_id);
        if (idx === -1) return;
        const next = e.key === 'ArrowRight'
          ? playable[(idx + 1) % playable.length]
          : playable[(idx - 1 + playable.length) % playable.length];
        e.preventDefault();
        setPopupVideo(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popupVideo, closePopupVideo, videos]);

  // Inline song popup player — same idea as the video popup. Clicking a song
  // card now opens a quick-preview overlay instead of forcing a /song/<id>
  // navigation (which requires a full /dashboard reload to return).
  const [popupSong, setPopupSong] = useState(null);
  const popupAudioRef = useRef(null);

  const closePopupSong = useCallback(() => {
    try {
      if (popupAudioRef.current) {
        popupAudioRef.current.pause();
        popupAudioRef.current.currentTime = 0;
      }
    } catch { /* pause() on a detached / not-yet-loaded media element is safe to ignore */ }
    setPopupSong(null);
  }, []);

  useEffect(() => {
    if (!popupSong) return;
    const onKey = (e) => {
      if (e.key === 'Escape') return closePopupSong();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const playable = songs.filter(s => s.status === 'completed' && s.audio_url);
        if (playable.length < 2) return;
        const idx = playable.findIndex(s => s.song_id === popupSong.song_id);
        if (idx === -1) return;
        const next = e.key === 'ArrowRight'
          ? playable[(idx + 1) % playable.length]
          : playable[(idx - 1 + playable.length) % playable.length];
        e.preventDefault();
        setPopupSong(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popupSong, closePopupSong, songs]);
  const [playingSongId, setPlayingSongId] = useState(null);

  // Community waitlist modal
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);

  // v3.0.134 — Playlists Phase 1: multi-select mode for bulk "Add to Playlist"
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSongIds, setSelectedSongIds] = useState([]);
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);

  // v3.0.141 — user-created categories
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [categoriesRefreshTrigger, setCategoriesRefreshTrigger] = useState(0);
  const bumpCategoriesRefresh = useCallback(() => {
    setCategoriesRefreshTrigger((n) => n + 1);
  }, []);

  // v3.0.136 — "Adding to playlist X" focused flow. Triggered by
  // /dashboard?addTo=<playlist_id> from the empty-state CTA on
  // /playlist/:id. Auto-enables select mode + shows a persistent
  // banner + swaps the action bar into a direct-add-to-X button.
  const addToPlaylistId = searchParams.get('addTo');
  const [addToPlaylist, setAddToPlaylist] = useState(null);
  const [addingBulk, setAddingBulk] = useState(false);

  const toggleSelectSong = useCallback((songId) => {
    setSelectedSongIds((prev) => (
      prev.includes(songId) ? prev.filter((id) => id !== songId) : [...prev, songId]
    ));
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedSongIds([]);
  }, []);

  // Load the playlist name when addTo is present + auto-enable select mode.
  useEffect(() => {
    if (!addToPlaylistId) {
      setAddToPlaylist(null);
      return;
    }
    setSelectMode(true);
    setSelectedSongIds([]);
    (async () => {
      try {
        const { data } = await apiFetch(`/playlists/${addToPlaylistId}`);
        setAddToPlaylist(data);
      } catch (err) {
        toast.error(`Couldn't load playlist: ${err.message || err}`);
        setAddToPlaylist(null);
      }
    })();
  }, [addToPlaylistId]);

  // Direct add-to-target-playlist (no dialog). Redirect back to the
  // playlist when done so users see their new tracks land.
  const bulkAddToTargetPlaylist = useCallback(async () => {
    if (!addToPlaylistId || selectedSongIds.length === 0) return;
    setAddingBulk(true);
    try {
      await apiFetch(`/playlists/${addToPlaylistId}/songs`, {
        method: 'POST',
        body: JSON.stringify({ song_ids: selectedSongIds }),
      });
      const n = selectedSongIds.length;
      toast.success(`Added ${n} song${n === 1 ? '' : 's'} to "${addToPlaylist?.name || 'playlist'}"`);
      exitSelectMode();
      navigate(`/playlist/${addToPlaylistId}`);
    } catch (err) {
      const msg = err.message || String(err);
      if (msg.toLowerCase().includes('no new songs')) {
        toast.info('All selected songs are already in that playlist');
      } else {
        toast.error(`Failed to add: ${msg}`);
      }
    } finally {
      setAddingBulk(false);
    }
  }, [addToPlaylistId, addToPlaylist, selectedSongIds, exitSelectMode, navigate]);
  const [alreadyOnWaitlist, setAlreadyOnWaitlist] = useState(false);

  // Low credits threshold
  const LOW_CREDITS_THRESHOLD = 10;

  const fetchData = useCallback(async () => {
    // 401 handler — without this, the .catch() calls on each apiFetch below
    // silently swallow 401s and we render an empty "0 songs 0 videos"
    // dashboard. Return null to signal "stale auth race — keep existing
    // state, don't overwrite with empties".
    // v2.9.45: Don't call refreshUser on 401 — that triggers checkAuth
    // which can clear the token after 2 consecutive 401s and bounce the
    // user mid-render. Just signal "not loaded" so we keep stale state.
    const handleUnauthorized = (err) => {
      if (err?.response?.status === 401) {
        return null; // signal: treat as not-loaded, do NOT setSongs([])
      }
      return { data: [] }; // genuine other error — fall back to empty
    };

    try {
      const [songsResult, videosResult, packagesResult, costsResult] = await Promise.all([
        apiFetch('/songs').catch(handleUnauthorized),
        apiFetch('/videos').catch(handleUnauthorized),
        apiFetch('/credits/packages').catch(e => ({ data: [] })),
        apiFetch('/credits/costs').catch(e => ({ data: {} }))
      ]);

      // If ANY of the core list fetches returned null (401), abort the render.
      // Don't overwrite stale-but-correct local state with empty arrays.
      if (songsResult === null || videosResult === null) {
        return;
      }

      setSongs(songsResult.data || []);
      setVideos(videosResult.data || []);
      if (packagesResult.data?.length) {
        setPackages(packagesResult.data);
      }
      if (costsResult.data?.song_unlock !== undefined) {
        setUnlockCost(costsResult.data.song_unlock);
      }
      if (costsResult.data?.song_generation !== undefined) {
        setSongGenerationCost(costsResult.data.song_generation);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wait for AuthContext to finish bootstrapping before firing the dashboard
    // queries. Without this gate, the queries race the auth check on cold
    // page-loads; tokens not yet attached, requests come back 401, and the
    // dashboard renders "0 songs 0 videos" until the user manually refreshes.
    if (authLoading) return;
    if (!user) return; // ProtectedRoute will redirect — don't fetch
    fetchData();
  }, [fetchData, authLoading, user]);

  // Refetch data when page becomes visible (e.g., user returns from another tab/page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Same auth gate as the initial fetch — avoid 401-storm when refocusing
      // a tab whose auth state is mid-rehydration.
      if (document.visibilityState === 'visible' && !authLoading && user) {
        fetchData();
      }
    };
    
    const handleFocus = () => {
      if (!authLoading && user) {
        fetchData();
      }
    };

    // bfcache restoration (v2.9.49) — Back/Forward sometimes restores
    // the page from memory with stale state (e.g. "0 songs" left over
    // from a mid-flight 401). pageshow.persisted=true is the only
    // reliable signal for that path; refetch when it fires.
    const handlePageShow = (e) => {
      if (e.persisted && !authLoading && user) {
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [fetchData, authLoading, user]);
  // Show low credits toast notification on first load
  useEffect(() => {
    if (!loading && user && user.credits < LOW_CREDITS_THRESHOLD && !lowCreditsToastShown) {
      setLowCreditsToastShown(true);
      toast.warning(
        <div className="flex items-center gap-3">
          <span>You're running low on credits ({user.credits} remaining)</span>
          <button
            onClick={() => {
              setShowBuyCredits(true);
              toast.dismiss();
            }}
            className="px-3 py-1 bg-primary text-white text-sm rounded-full font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Buy Now
          </button>
        </div>,
        {
          duration: 8000,
          id: 'low-credits-toast'
        }
      );
    }
  }, [loading, user, lowCreditsToastShown]);

  // Handle joining community waitlist
  const handleJoinWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      await apiFetch('/community/waitlist', { method: 'POST' });
      setAlreadyOnWaitlist(true);
      toast.success("You're on the list! We'll notify you when Community launches.");
    } catch (error) {
      if (error.response?.data?.detail?.includes('already')) {
        setAlreadyOnWaitlist(true);
        toast.info("You're already on the waitlist!");
      } else {
        toast.error('Failed to join waitlist');
      }
    } finally {
      setJoiningWaitlist(false);
    }
  };

  // Handle payment success/cancel from URL
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    const paymentCancelled = searchParams.get('payment_cancelled');
    const paymentId = searchParams.get('paymentId');
    const payerID = searchParams.get('PayerID');

    if (paymentSuccess && paymentId && payerID) {
      // Execute PayPal payment
      const executePayment = async () => {
        try {
          const { data } = await apiFetch(`/payments/execute?payment_id=${paymentId}&payer_id=${payerID}`, {
            method: 'POST'
          });
          if (data.payment_status === 'paid') {
            toast.success('Payment successful! Credits added to your account.');
            refreshUser();
          }
        } catch (error) {
          console.error('Error executing payment:', error);
          toast.error('Payment verification failed');
        }
      };
      executePayment();
      
      // Clean URL
      window.history.replaceState({}, '', '/dashboard');
    } else if (paymentCancelled) {
      toast.info('Payment cancelled');
      window.history.replaceState({}, '', '/dashboard');
    }
    
    // Check if buyCredits param is set
    const buyCredits = searchParams.get('buyCredits');
    if (buyCredits === 'true') {
      setShowBuyCredits(true);
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [searchParams, refreshUser]);

  // Open delete confirmation dialog
  const openDeleteDialog = (type, id, title) => {
    setDeleteDialog({ open: true, type, id, title });
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, type: null, id: null, title: '' });
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
    const { type, id } = deleteDialog;
    closeDeleteDialog();
    
    if (type === 'song') {
      try {
        await apiFetch(`/songs/${id}`, { method: 'DELETE' });
        setSongs(songs.filter(s => s.song_id !== id));
        toast.success('Moved to trash — kept for 30 days', {
          action: {
            label: 'View trash',
            onClick: () => window.location.assign('/trash'),
          },
        });
        // v3.0.142 — deleted song may have been in one or more custom
        // categories; backend decrements counts, refresh the popover.
        bumpCategoriesRefresh();
      } catch (error) {
        toast.error('Failed to delete song');
      }
    } else if (type === 'video') {
      try {
        await apiFetch(`/videos/${id}`, { method: 'DELETE' });
        setVideos(videos.filter(v => v.video_id !== id));
        toast.success('Video deleted permanently');
      } catch (error) {
        toast.error('Failed to delete video');
      }
    }
  };

  // Handle song unlock
  const handleUnlockSong = async (songId) => {
    setUnlockingSongId(songId);
    try {
      const { data } = await apiFetch(`/songs/${songId}/unlock`, { method: 'POST' });
      toast.success(data.message || 'Song unlocked!');
      refreshUser();
      // Update the song in the list
      setSongs(songs.map(s => 
        s.song_id === songId ? { ...s, locked: false } : s
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unlock failed');
    } finally {
      setUnlockingSongId(null);
    }
  };

  // Handle play/pause audio
  const handlePlayPause = (song) => {
    if (playingSongId === song.song_id) {
      // Pause current song
      audioRef.current?.pause();
      setPlayingSongId(null);
    } else {
      // Play new song
      if (audioRef.current) {
        audioRef.current.src = song.audio_url;
        audioRef.current.play();
        setPlayingSongId(song.song_id);
      }
    }
  };

  // v2.9.50 — Song Organization helpers
  // ------------------------------------------------------------------
  // Persist the active category pill to localStorage.
  const handleCategoryChange = (cat) => {
    setCategoryFilter(cat);
    try { localStorage.setItem('wim_song_category', cat); } catch (e) { console.debug("localStorage write failed:", e); }
  };

  const handleMoodChange = (v) => {
    setMoodFilter(v);
    try { localStorage.setItem('wim_song_mood', v); } catch (e) { console.debug("localStorage write failed:", e); }
  };

  const handleGenreChange = (v) => {
    setGenreFilter(v);
    try { localStorage.setItem('wim_song_genre', v); } catch (e) { console.debug("localStorage write failed:", e); }
  };

  const clearAllFilters = () => {
    handleCategoryChange('all');
    handleMoodChange('');
    handleGenreChange('');
    setSongSearch('');
  };

  // v2.9.55 — Dashboard preset handlers (mirror of Library preset UI).
  // Backend: `library_presets` collection, scope="dashboard".
  const fetchDashPresets = useCallback(async () => {
    try {
      const { data } = await apiFetch('/library/presets?scope=dashboard');
      const list = data.presets || [];
      setDashPresets(list);
      if (typeof data.cap === 'number') setDashPresetCap(data.cap);
      // v2.9.57 — auto-apply default preset once on first fetch
      if (!dashDefaultAppliedRef.current) {
        const def = list.find((p) => p.is_default);
        if (def) {
          const f = def.filters || {};
          // Apply silently (no toast) — feels like the page just "remembered"
          setCategoryFilter(f.category || 'all');
          try { localStorage.setItem('wim_song_category', f.category || 'all'); } catch (e) { console.debug("localStorage write failed:", e); }
          setMoodFilter(f.mood || '');
          try { localStorage.setItem('wim_song_mood', f.mood || ''); } catch (e) { console.debug("localStorage write failed:", e); }
          setGenreFilter(f.genre || '');
          try { localStorage.setItem('wim_song_genre', f.genre || ''); } catch (e) { console.debug("localStorage write failed:", e); }
          setSongSearch(f.search || '');
        }
        dashDefaultAppliedRef.current = true;
      }
    } catch (e) { console.debug('dashboard default apply failed:', e); /* non-critical */ }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    fetchDashPresets();
  }, [fetchDashPresets, authLoading, user]);

  const applyDashPreset = (preset) => {
    const f = preset?.filters || {};
    handleCategoryChange(f.category || 'all');
    handleMoodChange(f.mood || '');
    handleGenreChange(f.genre || '');
    setSongSearch(f.search || '');
    toast.success(`Applied "${preset.name}"`);
  };

  const saveCurrentAsDashPreset = async () => {
    const name = newDashPresetName.trim();
    if (!name) { toast.error('Give your preset a name'); return; }
    if (savingDashPreset) return;
    setSavingDashPreset(true);
    try {
      await apiFetch('/library/presets', {
        method: 'POST',
        body: JSON.stringify({
          scope: 'dashboard',
          name,
          filters: {
            category: categoryFilter,
            mood: moodFilter,
            genre: genreFilter,
            search: songSearch,
          },
        }),
      });
      await fetchDashPresets();
      setNewDashPresetName('');
      setShowDashPresetInput(false);
      toast.success(`Saved "${name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save preset');
    } finally {
      setSavingDashPreset(false);
    }
  };

  const deleteDashPreset = async (preset_id) => {
    try {
      await apiFetch(`/library/presets/${preset_id}`, { method: 'DELETE' });
      setDashPresets(prev => prev.filter(p => p.preset_id !== preset_id));
      toast.success('Preset removed');
    } catch { toast.error('Failed to delete preset'); }
  };

  // v2.9.57 — Toggle default status. Only one default per scope at a time.
  const toggleDashDefault = async (preset) => {
    const next = !preset.is_default;
    // Optimistic — flip locally, only the affected one needs updating
    setDashPresets(prev => prev.map(p => ({
      ...p,
      is_default: p.preset_id === preset.preset_id ? next : (next ? false : p.is_default),
    })));
    try {
      await apiFetch(`/library/presets/${preset.preset_id}/default`, {
        method: 'PATCH',
        body: JSON.stringify({ is_default: next }),
      });
      toast.success(next ? `"${preset.name}" set as default` : `Default cleared`);
    } catch (err) {
      // Revert on failure
      setDashPresets(prev => prev.map(p => ({
        ...p,
        is_default: p.preset_id === preset.preset_id ? preset.is_default : p.is_default,
      })));
      toast.error('Failed to update default');
    }
  };

  const isDashPresetActive = (preset) => {
    const f = preset?.filters || {};
    return (
      (f.category || 'all') === categoryFilter &&
      (f.mood || '') === moodFilter &&
      (f.genre || '') === genreFilter &&
      (f.search || '') === songSearch
    );
  };

  const dashHasAnyFilter = categoryFilter !== 'all' || !!moodFilter || !!genreFilter || !!songSearch;

  // Toggle a song between "favorite" and "default" (the ★ button).
  const handleToggleFavorite = async (song) => {
    const next = (song.category === 'favorite') ? 'default' : 'favorite';
    setTogglingCategoryId(song.song_id);
    // Optimistic — flip locally first, revert on failure
    setSongs(prev => prev.map(s => s.song_id === song.song_id ? { ...s, category: next } : s));
    try {
      await apiFetch(`/songs/${song.song_id}/category`, {
        method: 'PATCH',
        body: JSON.stringify({ category: next }),
      });
      toast.success(next === 'favorite' ? 'Added to favorites' : 'Removed from favorites');
      // v3.0.142 — refresh built-in counts (Favorites/All) in the filter popover
      bumpCategoriesRefresh();
    } catch (err) {
      setSongs(prev => prev.map(s => s.song_id === song.song_id ? { ...s, category: song.category } : s));
      toast.error('Failed to update favorite');
    } finally {
      setTogglingCategoryId(null);
    }
  };

  // Toggle a song between "archive" and "default" (the 📦 button).
  const handleToggleArchive = async (song) => {
    const next = (song.category === 'archive') ? 'default' : 'archive';
    setTogglingCategoryId(song.song_id);
    setSongs(prev => prev.map(s => s.song_id === song.song_id ? { ...s, category: next } : s));
    try {
      await apiFetch(`/songs/${song.song_id}/category`, {
        method: 'PATCH',
        body: JSON.stringify({ category: next }),
      });
      toast.success(next === 'archive' ? 'Archived' : 'Restored from archive');
      // v3.0.142 — archived songs are hidden from custom-category views
      // and the Archive/All built-in counts flip, so refresh the popover.
      bumpCategoriesRefresh();
    } catch (err) {
      setSongs(prev => prev.map(s => s.song_id === song.song_id ? { ...s, category: song.category } : s));
      toast.error('Failed to update archive');
    } finally {
      setTogglingCategoryId(null);
    }
  };

  // v2.9.52 — Fetch Mood + Genre options from backend so dropdowns
  // stay in sync with whatever the admin has configured (same source
  // CreateSong uses, identical UX continuity).
  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiFetch('/song-options');
        if (cancelled) return;
        if (data?.moods?.length) setMoodOptions([...data.moods].sort());
        if (data?.genres?.length) setGenreOptions([...data.genres].sort());
      } catch (e) {
        console.debug('mood/genre options fetch failed:', e);
        // Non-critical — fallback defaults already in state.
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user]);

  // Apply ALL filters together (AND): category pill + mood + genre + search.
  // Songs without a `category` field default to "default" (back-compat
  // with pre-v2.9.50 rows).
  // Mood/genre match either the structured field OR the prompt/style text,
  // since older songs may have the mood/genre baked into the prompt only.
  const filteredSongs = React.useMemo(() => {
    const q = songSearch.trim().toLowerCase();
    const mood = moodFilter.trim().toLowerCase();
    const genre = genreFilter.trim().toLowerCase();
    const catOf = (s) => (s.category || 'default');
    return songs.filter(s => {
      // Category pill
      if (categoryFilter === 'all') {
        if (catOf(s) === 'archive') return false;
      } else if (categoryFilter === 'default') {
        if (catOf(s) !== 'default') return false;
      } else if (categoryFilter === 'favorite') {
        if (catOf(s) !== 'favorite') return false;
      } else if (categoryFilter === 'archive') {
        if (catOf(s) !== 'archive') return false;
      } else if (typeof categoryFilter === 'string' && categoryFilter.startsWith('cat_')) {
        // v3.0.141 — user-created custom category; song is a match iff
        // its categories array contains this id. Also exclude archived
        // songs from custom category views (they're deliberately hidden).
        if (catOf(s) === 'archive') return false;
        const arr = Array.isArray(s.categories) ? s.categories : [];
        if (!arr.includes(categoryFilter)) return false;
      }
      // Mood — match structured field OR prompt/style mention
      if (mood) {
        const songMood = (s.mood || '').toLowerCase();
        if (songMood === mood) {
          // exact structured match — keep
        } else {
          const blob = [s.prompt, s.style].filter(Boolean).join(' ').toLowerCase();
          if (!blob.includes(mood)) return false;
        }
      }
      // Genre — same logic
      if (genre) {
        const songGenre = (s.genre || '').toLowerCase();
        if (songGenre === genre) {
          // exact match — keep
        } else {
          const blob = [s.prompt, s.style].filter(Boolean).join(' ').toLowerCase();
          if (!blob.includes(genre)) return false;
        }
      }
      // Search — title / prompt / style / mood / genre free-text
      if (q) {
        const blob = [s.title, s.prompt, s.style, s.mood, s.genre]
          .filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [songs, categoryFilter, moodFilter, genreFilter, songSearch]);

  // Counts for the filter pills (live, derived from songs state).
  const categoryCounts = React.useMemo(() => {
    const counts = { all: 0, default: 0, favorite: 0, archive: 0 };
    for (const s of songs) {
      const c = s.category || 'default';
      if (c === 'archive') counts.archive += 1;
      else if (c === 'favorite') { counts.favorite += 1; counts.all += 1; }
      else { counts.default += 1; counts.all += 1; }
    }
    return counts;
  }, [songs]);

  const createPayPalOrder = async (packageId) => {
    try {
      const { data } = await apiFetch('/payments/create-order', {
        method: 'POST',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });
      // Redirect to PayPal approval URL
      window.location.href = data.approval_url;
    } catch (error) {
      console.error('PayPal error:', error);
      toast.error('Failed to start payment');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'processing':
        return 'status-processing';
      case 'failed':
        return 'status-failed';
      default:
        return 'status-pending';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {[0, 0.1, 0.2, 0.3, 0.4].map((delay, i) => (
              <div key={i} className="audio-bar" style={{ animationDelay: `${delay}s` }}></div>
            ))}
          </div>
          <span className="text-white">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setPlayingSongId(null)}
        onError={() => setPlayingSongId(null)}
      />
      
      {/* Navigation */}
      <MainNav />

      {/* Main Content */}
      <main className="pt-24 pb-12 container-wide">
        {/* Low Credits Warning Banner */}
        {user && user.credits < LOW_CREDITS_THRESHOLD && (
          <div 
            className="mb-6 p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl flex items-center justify-between gap-4"
            data-testid="low-credits-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-white font-medium">Running low on credits!</p>
                <p className="text-sm text-yellow-200/70">
                  You have <span className="font-bold text-yellow-400">{user.credits} credits</span> remaining. 
                  Song generation costs 20 credits.
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setShowBuyCredits(true)}
              className="btn-primary flex-shrink-0"
              data-testid="quick-buy-credits-btn"
            >
              <Zap className="h-4 w-4 mr-2" />
              Buy Credits
            </Button>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-2 flex-wrap">
            <span>Welcome, {user?.name?.split(' ')[0]} 👋</span>
            <CoachEye targetId="dashboard.start" label="Not sure where to start?" className="align-middle" />
          </h1>
          <p className="text-xl text-zinc-300 mb-2">Let's make your next song 🎵</p>
          <p className="text-zinc-400">
            You've got <span className="text-primary font-semibold">{user?.credits || 0} credits</span>—create something awesome
          </p>
          {user?.credits > 0 && songGenerationCost > 0 && (
            <p className="text-zinc-500 text-sm">🎵 Enough for ~{Math.floor(user.credits / songGenerationCost)} songs</p>
          )}
          <p className="text-zinc-500 text-sm mt-1">🎵 Most songs are created in under a minute</p>
        </div>

        {/* Start Creating Section */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-white mb-4">🔥 Start creating</h2>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button 
            onClick={() => navigate('/create-song')}
            className="glass-card p-6 text-left card-hover group"
            data-testid="create-song-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                <Music className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">🎵 Make a Song</h3>
                <p className="text-xs text-zinc-400">Generate music</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/create-from-image')}
            className="glass-card p-6 text-left card-hover group"
            data-testid="create-from-image-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                <Image className="h-6 w-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">📷 Song from Image</h3>
                <p className="text-xs text-zinc-400">Upload, get a song</p>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/create-cover')}
            className="glass-card p-6 text-left card-hover group"
            data-testid="create-cover-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center group-hover:bg-pink-500/30 transition-colors">
                <Image className="h-6 w-6 text-pink-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">🎨 Create Cover Art</h3>
                <p className="text-xs text-zinc-400">Generate artwork</p>
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/create-gift')}
            className="glass-card p-6 text-left card-hover group"
            data-testid="create-gift-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                <Gift className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">🎁 Send a Music Gift</h3>
                <p className="text-xs text-zinc-400">Share music</p>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/birthday')}
            className="glass-card p-6 text-left card-hover group relative overflow-hidden"
            data-testid="birthday-song-card"
          >
            <div className="absolute -top-1 -right-1 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg animate-pulse shadow-lg">
              ✨ FREE
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center group-hover:from-pink-500/30 group-hover:to-purple-500/30 transition-colors">
                <span className="text-2xl">🎂</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-white">🎂 Make a Birthday Song 🎉</h3>
                <p className="text-xs text-zinc-400">Send a Musical Birthday Card</p>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/create-video')}
            className="glass-card p-6 text-left card-hover group relative"
            data-testid="create-video-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center group-hover:bg-secondary/30 transition-colors">
                <Video className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">🎬 Slideshow Video</h3>
                <p className="text-xs text-zinc-400">Photos + Video Clips + Music</p>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/library')}
            className="glass-card p-6 text-left card-hover group relative"
            data-testid="explore-library-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                <Headphones className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">🎧 Explore Library</h3>
                <p className="text-xs text-zinc-400">Discover & collect songs</p>
              </div>
            </div>
          </button>
          
          {/* Community - Coming Soon (dimmed) */}
          <button 
            onClick={() => setShowCommunityModal(true)}
            className="glass-card p-6 text-left group relative opacity-60 hover:opacity-80 transition-opacity"
            data-testid="community-card"
          >
            <div className="absolute -top-1 -right-1 bg-zinc-700 text-zinc-300 text-xs font-medium px-2 py-0.5 rounded-bl-lg rounded-tr-xl">
              Coming Soon
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-zinc-600/20 flex items-center justify-center group-hover:bg-zinc-600/30 transition-colors">
                <span className="text-xl">💬</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-zinc-300">💬 Community</h3>
                <p className="text-xs text-zinc-500">Connect with creators</p>
              </div>
            </div>
          </button>
        </div>

        {/* Quick Start Row */}
        <div className="mb-8 p-4 glass-card rounded-xl">
          <p className="text-white font-medium mb-3">🎯 Not sure where to start?</p>
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => navigate('/create-song?quickstart=birthday')}
              className="px-4 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-full text-sm font-medium transition-colors"
            >
              🎂 Birthday song
            </button>
            <button 
              onClick={() => navigate('/create-song?quickstart=love')}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-full text-sm font-medium transition-colors"
            >
              ❤️ Love song
            </button>
            <button 
              onClick={() => navigate('/create-song?quickstart=funny')}
              className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 rounded-full text-sm font-medium transition-colors"
            >
              🎉 Funny song
            </button>
            <button 
              onClick={() => navigate('/create-song?quickstart=dog')}
              className="px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 rounded-full text-sm font-medium transition-colors"
            >
              🐕 Pet Dog Song
            </button>
          </div>
        </div>

        {/* Songs Section Header */}
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-white">Your Songs 🎶</h2>
            <p className="text-zinc-400 text-sm">Pick up where you left off or revisit your favorites</p>
          </div>
          {/* v3.0.134 — Playlists entry point + multi-select toggle.
              v3.0.136 — Hidden when in the focused "Add to X" flow so
              the banner is the only status the user sees. */}
          {activeTab === 'songs' && songs.length > 0 && !addToPlaylistId && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => navigate('/playlists')}
                className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 hover:text-white"
                data-testid="my-playlists-nav-btn"
              >
                <ListMusic className="h-4 w-4 mr-1.5" />
                My Playlists
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/trash')}
                className="border-white/20 text-zinc-300 hover:bg-white/10 hover:text-white"
                data-testid="dashboard-view-trash-btn"
                title="See songs you've deleted in the last 30 days"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Recently deleted
              </Button>
              <Button
                variant={selectMode ? 'default' : 'outline'}
                onClick={() => {
                  if (selectMode) exitSelectMode();
                  else setSelectMode(true);
                }}
                className={selectMode
                  ? 'bg-emerald-500 text-black hover:bg-emerald-600'
                  : 'border-white/20 text-zinc-300 hover:bg-white/10 hover:text-white'}
                data-testid="select-mode-toggle"
              >
                {selectMode ? (
                  <><X className="h-4 w-4 mr-1.5" /> Cancel</>
                ) : (
                  <><CheckSquare className="h-4 w-4 mr-1.5" /> Select</>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* v3.0.136 — Focused "Add to X" banner. Only shown when
            arriving from an empty playlist's Pick songs CTA. */}
        {addToPlaylistId && (
          <div
            className="mb-4 flex items-center gap-3 flex-wrap bg-emerald-500/10 border border-emerald-500/40 rounded-lg px-4 py-3"
            data-testid="add-to-playlist-banner"
          >
            <ListMusic className="h-5 w-5 text-emerald-300 shrink-0" />
            <div className="flex-1 min-w-[220px] text-sm">
              <div className="text-emerald-200 font-medium">
                Adding to "<span data-testid="add-to-playlist-name">{addToPlaylist?.name || '…'}</span>"
              </div>
              <div className="text-xs text-emerald-100/70">
                Tap songs below to select them, then hit "Add {selectedSongIds.length || ''} song{selectedSongIds.length === 1 ? '' : 's'}".
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate(`/playlist/${addToPlaylistId}`)}
              className="text-emerald-200 hover:bg-emerald-500/20"
              data-testid="add-to-playlist-cancel"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-white/10">
          <button 
            onClick={() => setActiveTab('songs')}
            className={`pb-4 px-2 text-sm font-medium transition-colors ${
              activeTab === 'songs' 
                ? 'text-white border-b-2 border-primary' 
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="songs-tab"
          >
            Songs ({songs.length})
          </button>
          <button 
            onClick={() => setActiveTab('videos')}
            className={`pb-4 px-2 text-sm font-medium transition-colors ${
              activeTab === 'videos' 
                ? 'text-white border-b-2 border-primary' 
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="videos-tab"
          >
            Videos ({videos.length})
          </button>
        </div>

        {/* Songs List */}
        {activeTab === 'songs' && (
          <div data-testid="songs-list">
            {/* v2.9.50 — Song Organization toolbar: pills + search + chips */}
            {songs.length > 0 && (
              <div className="mb-4 space-y-3" data-testid="song-organization-toolbar">
                {/* v3.0.141 — Filter popover replaces the fixed
                    All/Favorites/Archive pills, adds user-created
                    custom categories. */}
                <div className="flex flex-wrap items-center gap-2">
                  <CategoriesFilterPopover
                    activeCategoryId={categoryFilter}
                    onChange={handleCategoryChange}
                    onManage={() => setShowManageCategories(true)}
                    refreshTrigger={categoriesRefreshTrigger}
                  />

                  {/* Search box pushes right */}
                  <div className="relative ml-auto w-full sm:w-64">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500 pointer-events-none" />
                    <input
                      type="text"
                      value={songSearch}
                      onChange={(e) => setSongSearch(e.target.value)}
                      placeholder="Search title, prompt, style…"
                      data-testid="song-search-input"
                      className="w-full bg-white/5 border border-white/10 rounded-full pl-8 pr-8 py-1.5 text-xs text-white placeholder:text-zinc-500 focus:outline-none focus:border-primary/50"
                    />
                    {songSearch && (
                      <button
                        type="button"
                        onClick={() => setSongSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                        data-testid="song-search-clear"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* v2.9.52 — Mood + Genre dropdowns (same options as Create Song).
                    Filters AND together with the pill, search, and each other. */}
                <div className="flex flex-wrap items-center gap-2" data-testid="song-filter-dropdowns">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Mood</label>
                  <select
                    value={moodFilter}
                    onChange={(e) => handleMoodChange(e.target.value)}
                    data-testid="song-mood-filter"
                    className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-primary/50 capitalize min-w-[8rem]"
                  >
                    <option value="" className="bg-zinc-900 text-white">Any mood</option>
                    {moodOptions.map((m) => (
                      <option key={m} value={m} className="bg-zinc-900 text-white capitalize">{m}</option>
                    ))}
                  </select>

                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 ml-2">Genre</label>
                  <select
                    value={genreFilter}
                    onChange={(e) => handleGenreChange(e.target.value)}
                    data-testid="song-genre-filter"
                    className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-primary/50 capitalize min-w-[8rem]"
                  >
                    <option value="" className="bg-zinc-900 text-white">Any genre</option>
                    {genreOptions.map((g) => (
                      <option key={g} value={g} className="bg-zinc-900 text-white capitalize">{g}</option>
                    ))}
                  </select>
                </div>

                {/* v2.9.55 — Filter presets row (mirrors Library presets). */}
                <div className="flex flex-wrap items-center gap-1.5" data-testid="dash-presets-row">
                  {dashPresets.length > 0 && (
                    <>
                      <Bookmark className="h-3 w-3 text-zinc-500" />
                      {dashPresets.map((p) => {
                        const active = isDashPresetActive(p);
                        return (
                          <span
                            key={p.preset_id}
                            className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-0.5 text-[11px] border transition-colors ${
                              active
                                ? 'bg-primary/25 border-primary/50 text-primary'
                                : 'bg-white/5 border-white/10 text-zinc-300 hover:border-primary/40 hover:text-white'
                            }`}
                            data-testid={`dash-preset-${p.preset_id}`}
                          >
                            <button
                              type="button"
                              onClick={() => applyDashPreset(p)}
                              className="truncate max-w-[160px]"
                              title={`Apply "${p.name}"`}
                            >
                              {p.name}
                            </button>
                            {/* v2.9.57 — Default toggle (star). Only one default per scope. */}
                            <button
                              type="button"
                              onClick={() => toggleDashDefault(p)}
                              className={`transition-opacity ${
                                p.is_default
                                  ? 'text-yellow-400 hover:text-yellow-300'
                                  : 'text-zinc-500 hover:text-yellow-400 opacity-60 hover:opacity-100'
                              }`}
                              title={p.is_default ? 'Default preset — auto-applies on login' : 'Set as default (auto-apply on login)'}
                              data-testid={`dash-preset-default-${p.preset_id}`}
                            >
                              <Star className="h-3 w-3" fill={p.is_default ? 'currentColor' : 'none'} />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteDashPreset(p.preset_id)}
                              className="opacity-50 hover:opacity-100 hover:text-red-400 transition-opacity"
                              title="Delete preset"
                              data-testid={`dash-preset-delete-${p.preset_id}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </>
                  )}

                  {!showDashPresetInput ? (
                    <button
                      type="button"
                      disabled={!dashHasAnyFilter || dashPresets.length >= dashPresetCap}
                      onClick={() => setShowDashPresetInput(true)}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] border transition-colors ${
                        !dashHasAnyFilter || dashPresets.length >= dashPresetCap
                          ? 'bg-white/5 border-white/10 text-zinc-600 cursor-not-allowed'
                          : 'bg-white/5 border-primary/40 text-primary hover:bg-primary/15'
                      }`}
                      title={
                        !dashHasAnyFilter
                          ? 'Pick at least one filter to save'
                          : dashPresets.length >= dashPresetCap
                          ? `Limit reached (${dashPresetCap} presets — delete one first)`
                          : 'Save current filters as a preset'
                      }
                      data-testid="dash-preset-save-open"
                    >
                      <Bookmark className="h-3 w-3" />
                      Save as preset
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-primary/40 pl-2 pr-1 py-0.5">
                      <input
                        type="text"
                        value={newDashPresetName}
                        onChange={(e) => setNewDashPresetName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveCurrentAsDashPreset();
                          if (e.key === 'Escape') { setShowDashPresetInput(false); setNewDashPresetName(''); }
                        }}
                        placeholder="Preset name"
                        maxLength={40}
                        autoFocus
                        data-testid="dash-preset-name-input"
                        className="bg-transparent text-[11px] text-white placeholder:text-zinc-500 focus:outline-none w-32"
                      />
                      <button
                        type="button"
                        onClick={saveCurrentAsDashPreset}
                        disabled={savingDashPreset || !newDashPresetName.trim()}
                        className="text-primary hover:text-white disabled:text-zinc-600 text-[11px] px-1.5"
                        data-testid="dash-preset-name-confirm"
                      >
                        {savingDashPreset ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowDashPresetInput(false); setNewDashPresetName(''); }}
                        className="text-zinc-500 hover:text-red-400"
                        data-testid="dash-preset-name-cancel"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                </div>

                {/* Result count indicator (only when filtering changes the set) */}
                {(categoryFilter !== 'all' || songSearch || moodFilter || genreFilter) && (
                  <p className="text-xs text-zinc-500" data-testid="song-filter-result-count">
                    Showing {filteredSongs.length} of {songs.length} songs
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="ml-2 text-primary hover:underline"
                      data-testid="song-filter-clear"
                    >
                      Clear filters
                    </button>
                  </p>
                )}
              </div>
            )}

            {songs.length === 0 ? (
              <div className="empty-state glass-card py-16">
                <Music className="h-16 w-16 text-zinc-600 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No songs yet</h3>
                <p className="text-zinc-400 mb-6">Create your first AI-generated song</p>
                <Button onClick={() => navigate('/create-song')} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Song
                </Button>
              </div>
            ) : filteredSongs.length === 0 ? (
              <div className="empty-state glass-card py-12" data-testid="songs-empty-filtered">
                <Music className="h-12 w-12 text-zinc-600 mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">
                  {categoryFilter === 'favorite' && !moodFilter && !genreFilter && !songSearch && 'No favorites yet'}
                  {categoryFilter === 'archive' && !moodFilter && !genreFilter && !songSearch && 'Nothing archived'}
                  {(moodFilter || genreFilter || (categoryFilter === 'all' && songSearch)) && 'No songs match these filters'}
                </h3>
                <p className="text-zinc-400 mb-4 text-sm">
                  {categoryFilter === 'favorite' && !moodFilter && !genreFilter && !songSearch && 'Click the ☆ icon on any song to add it here.'}
                  {categoryFilter === 'archive' && !moodFilter && !genreFilter && !songSearch && 'Archive old songs to keep your dashboard tidy.'}
                  {(moodFilter || genreFilter || (categoryFilter === 'all' && songSearch)) && 'Try clearing one of the filters.'}
                </p>
                <Button
                  variant="ghost"
                  onClick={clearAllFilters}
                  className="text-primary hover:text-primary/80"
                >
                  Clear all filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {filteredSongs.map((song) => {
                  const isSelected = selectedSongIds.includes(song.song_id);
                  return (
                  <div 
                    key={song.song_id}
                    className={`glass-card overflow-hidden card-hover group cursor-pointer relative transition-all ${
                      selectMode
                        ? (isSelected ? 'ring-2 ring-emerald-400' : 'opacity-50 hover:opacity-80')
                        : ''
                    }`}
                    onClick={() => {
                      if (selectMode) {
                        toggleSelectSong(song.song_id);
                        return;
                      }
                      // Open quick-preview popup for completed songs; route to
                      // detail page only for processing/failed (where popup is
                      // not useful and detail shows logs/retry actions).
                      if (song.status === 'completed' && song.audio_url) {
                        setPopupSong(song);
                      } else {
                        navigate(`/song/${song.song_id}`);
                      }
                    }}
                    data-testid={`song-card-${song.song_id}`}
                  >
                    {/* Thumbnail - square for compact view */}
                    <div className="aspect-square relative bg-gradient-to-br from-primary/20 to-secondary/20">
                      {song.image_url ? (
                        <img src={song.image_url} alt={song.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music className="h-8 w-8 text-white/20" />
                        </div>
                      )}
                      <div className="absolute inset-0 song-card-overlay"></div>
                      
                      {/* Locked badge on thumbnail */}
                      {song.status === 'completed' && song.locked && (
                        <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-yellow-500/90 text-black text-[10px] font-bold rounded flex items-center gap-0.5">
                          <Lock className="h-2.5 w-2.5" />
                        </div>
                      )}

                      {/* v2.9.50 — Favorite star toggle. Always interactive,
                          filled when category=favorite. Positioned bottom-left
                          so it doesn't clash with lock/status badges.
                          v3.0.134 — hidden in selectMode. */}
                      {!selectMode && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleToggleFavorite(song); }}
                        disabled={togglingCategoryId === song.song_id}
                        title={song.category === 'favorite' ? 'Remove from favorites' : 'Add to favorites'}
                        data-testid={`favorite-toggle-${song.song_id}`}
                        className={`absolute bottom-1 left-1 z-10 p-1 rounded-full transition-all ${
                          song.category === 'favorite'
                            ? 'bg-yellow-400/90 text-black opacity-100'
                            : 'bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-black/80'
                        }`}
                      >
                        <Star className="h-3 w-3" fill={song.category === 'favorite' ? 'currentColor' : 'none'} />
                      </button>
                      )}
                      
                      {/* Status badge for processing/failed */}
                      {song.status !== 'completed' && (
                        <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold flex items-center gap-0.5 ${
                          song.status === 'processing' ? 'bg-blue-500/90 text-white' : 'bg-red-500/90 text-white'
                        }`}>
                          {getStatusIcon(song.status)}
                        </div>
                      )}
                      
                      
                      {/* Action buttons overlay on hover (stays visible when playing).
                          v3.0.134 — hidden in selectMode so clicks reliably toggle selection. */}
                      {!selectMode && (
                      <div className={`absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent transition-opacity ${
                        playingSongId === song.song_id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}>
                        <div className="flex flex-wrap gap-1 justify-center">
                          {/* Play/Pause button - plays audio inline */}
                          {song.status === 'completed' && song.audio_url && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePlayPause(song); }}
                              className={`px-2 py-1 ${playingSongId === song.song_id ? 'bg-primary hover:bg-primary/80' : 'bg-white/20 hover:bg-white/30'} text-white rounded text-[10px] font-medium transition-colors flex items-center gap-1`}
                            >
                              {playingSongId === song.song_id ? (
                                <><Pause className="h-3 w-3" /> Pause</>
                              ) : (
                                <><Play className="h-3 w-3" /> Play</>
                              )}
                            </button>
                          )}
                          
                          {/* View button - goes to song detail page */}
                          {song.status === 'completed' && song.audio_url && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/song/${song.song_id}`); }}
                              className="px-2 py-1 bg-blue-500/80 hover:bg-blue-500 text-white rounded text-[10px] font-medium transition-colors flex items-center gap-1"
                            >
                              <Eye className="h-3 w-3" /> View
                            </button>
                          )}
                          
                          {/* Download button - direct download for unlocked songs */}
                          {song.status === 'completed' && song.audio_url && !song.locked && (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/songs/${song.song_id}/download-with-cover`}
                              download={`${song.title || 'song'}.mp3`}
                              onClick={(e) => e.stopPropagation()}
                              className="px-2 py-1 bg-green-500/80 hover:bg-green-500 text-white rounded text-[10px] font-medium transition-colors flex items-center gap-1"
                            >
                              <Download className="h-3 w-3" /> Download
                            </a>
                          )}
                          
                          {/* Share button for unlocked songs */}
                          {song.status === 'completed' && song.audio_url && !song.locked && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <ShareButtons type="song" id={song.song_id} title={song.title} compact={true} />
                            </div>
                          )}
                          
                          {/* Keep This Song (Unlock) for locked songs */}
                          {song.status === 'completed' && song.audio_url && song.locked && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleUnlockSong(song.song_id); }}
                              disabled={unlockingSongId === song.song_id}
                              className="px-2 py-1 bg-yellow-500/80 hover:bg-yellow-500 text-black rounded text-[10px] font-medium transition-colors flex items-center gap-1"
                              title={`Unlock full song (${unlockCost} credits)`}
                              data-testid={`unlock-song-${song.song_id}`}
                            >
                              {unlockingSongId === song.song_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>🔓 Keep This Song 🎵</>
                              )}
                            </button>
                          )}
                          
                          {/* Create Shareable Video for unlocked songs */}
                          {song.status === 'completed' && song.audio_url && !song.locked && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/create-video?song=${song.song_id}`); }}
                              className="px-2 py-1 bg-purple-500/80 hover:bg-purple-500 text-white rounded text-[10px] font-medium transition-colors flex items-center gap-1"
                            >
                              🎥 Create Video
                            </button>
                          )}
                        </div>
                      </div>
                      )}
                      
                      {/* Delete + Archive buttons - top right.
                          v3.0.134 — hidden in selectMode so clicks always toggle selection.
                          v3.0.141 — added a "Categorize" tag button. */}
                      {!selectMode && (
                      <div className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <CategorizeSongPopover
                          songId={song.song_id}
                          currentIds={Array.isArray(song.categories) ? song.categories : []}
                          onManage={() => setShowManageCategories(true)}
                          onChange={(next) => {
                            setSongs((prev) => prev.map((s) =>
                              s.song_id === song.song_id ? { ...s, categories: next } : s
                            ));
                            bumpCategoriesRefresh();
                          }}
                          trigger={
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="p-1.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded transition-colors"
                              title="Categorize song"
                              data-testid={`categorize-song-${song.song_id}`}
                            >
                              <Tag className="h-3 w-3" />
                            </button>
                          }
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleArchive(song); }}
                          disabled={togglingCategoryId === song.song_id}
                          className={`p-1.5 rounded transition-colors ${
                            song.category === 'archive'
                              ? 'bg-zinc-300 hover:bg-zinc-200 text-zinc-900'
                              : 'bg-zinc-700 hover:bg-zinc-600 text-white'
                          }`}
                          title={song.category === 'archive' ? 'Restore from archive' : 'Archive'}
                          data-testid={`archive-toggle-${song.song_id}`}
                        >
                          <Archive className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openDeleteDialog('song', song.song_id, song.title); }}
                          className="p-1.5 bg-red-500 hover:bg-red-400 text-white rounded transition-colors"
                          title="Delete song"
                          data-testid={`delete-song-${song.song_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      )}
                    </div>
                    {/* Card content - compact */}
                    <div className="p-2">
                      <h3 className="font-medium text-white text-xs truncate">{song.title}</h3>
                      <span className="text-[10px] text-zinc-500">
                        {new Date(song.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {/* v3.0.134 — Select-mode checkbox overlay */}
                    {selectMode && (
                      <div className="absolute inset-0 z-20 pointer-events-none">
                        <div
                          className={`absolute top-1.5 right-1.5 p-1 rounded ${
                            isSelected
                              ? 'bg-emerald-500 text-black'
                              : 'bg-black/70 text-white'
                          }`}
                          data-testid={`song-select-badge-${song.song_id}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="h-4 w-4" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );})}
              </div>
            )}
          </div>
        )}

        {/* Videos List */}
        {activeTab === 'videos' && (
          <div data-testid="videos-list">
            {videos.length === 0 ? (
              <div className="empty-state glass-card py-16">
                <Video className="h-16 w-16 text-zinc-600 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No videos yet</h3>
                <p className="text-zinc-400 mb-6">Create a slideshow from your photos and music</p>
                <Button onClick={() => navigate('/create-video')} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Slideshow
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map((video) => (
                  <div 
                    key={video.video_id}
                    className="glass-card overflow-hidden card-hover group"
                    data-testid={`video-card-${video.video_id}`}
                  >
                    <div className="aspect-video relative bg-gradient-to-br from-secondary/20 to-primary/20">
                      {video.video_url && video.status === 'completed' ? (
                        <video 
                          src={video.video_url.startsWith('http') ? video.video_url : `${process.env.REACT_APP_BACKEND_URL}${video.video_url}`}
                          className="w-full h-full object-cover"
                          muted
                          onMouseEnter={(e) => e.target.play()}
                          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-12 w-12 text-white/20" />
                        </div>
                      )}
                      <div className="absolute inset-0 song-card-overlay"></div>
                      {video.status === 'completed' && video.video_url && (
                        <button 
                          onClick={() => setPopupVideo(video)}
                          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`play-video-popup-${video.video_id}`}
                          title="Play in popup"
                        >
                          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                            <Play className="h-6 w-6 text-white ml-1" />
                          </div>
                        </button>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-bold text-white truncate">{video.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${getStatusBadgeClass(video.status)}`}>
                          {getStatusIcon(video.status)}
                          {video.status}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{video.prompt}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-500">
                          {video.duration}s • {video.size}
                        </span>
                        <div className="flex gap-2 items-center">
                          {video.status === 'completed' && video.video_url && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <ShareButtons type="video" id={video.video_id} title={video.title} videoUrl={video.video_url} />
                            </div>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/video/${video.video_id}`)}
                            className="text-zinc-400 hover:text-white"
                          >
                            View
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openDeleteDialog('video', video.video_id, video.title)}
                            className="text-zinc-400 hover:text-red-400"
                            data-testid={`delete-video-${video.video_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Buy Credits Modal with PayPal */}
      {showBuyCredits && (
        <div className="modal-overlay" onClick={() => { setShowBuyCredits(false); setSelectedPackage(null); }}>
          <div 
            className="glass-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="buy-credits-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Buy Credits</h2>
              <button 
                onClick={() => { setShowBuyCredits(false); setSelectedPackage(null); }}
                className="text-zinc-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {!selectedPackage ? (
              <div className="grid sm:grid-cols-3 gap-4">
                {packages.map((pkg) => (
                  <div 
                    key={pkg.package_id}
                    className={`pricing-card cursor-pointer ${pkg.package_id === 'creator' ? 'featured' : ''}`}
                    onClick={() => setSelectedPackage(pkg)}
                  >
                    {pkg.package_id === 'creator' && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-xs font-medium rounded-full">
                        Best Value
                      </div>
                    )}
                    <h3 className="font-bold text-white mb-1">{pkg.name}</h3>
                    <div className="text-3xl font-bold text-white mb-2">${pkg.price}</div>
                    <div className="credit-badge mb-4">
                      <Zap className="h-4 w-4" />
                      {pkg.credits} credits
                    </div>
                    <Button 
                      className={pkg.package_id === 'creator' ? 'btn-primary w-full' : 'btn-secondary w-full'}
                      data-testid={`select-${pkg.package_id}-btn`}
                    >
                      Select
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-bold text-white mb-2">{selectedPackage.name}</h3>
                  <div className="text-4xl font-bold text-primary mb-2">${selectedPackage.price}</div>
                  <div className="credit-badge mx-auto">
                    <Zap className="h-4 w-4" />
                    {selectedPackage.credits} credits
                  </div>
                </div>

                {/* TEMPORARY TEST MODE — only shown when the deploy explicitly
                    enables it via REACT_APP_ENABLE_TEST_CREDITS=true. On the
                    self-hosted production build this stays hidden, and the
                    backend `/test/add-credits` route 404s. */}
                {process.env.REACT_APP_ENABLE_TEST_CREDITS === 'true' && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                  <p className="text-yellow-400 text-sm text-center mb-3">🧪 TEST MODE - PayPal bypassed</p>
                  <Button
                    onClick={async () => {
                      try {
                        const { data } = await apiFetch('/test/add-credits', {
                          method: 'POST'
                        });
                        toast.success('100 test credits added!');
                        await refreshUser();
                        setShowBuyCredits(false);
                        setSelectedPackage(null);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to add credits');
                      }
                    }}
                    className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                  >
                    Add 100 Test Credits (Free)
                  </Button>
                </div>
                )}
                {/* END TEST MODE */}

                <div className="bg-white rounded-lg p-4">
                  <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "blue", shape: "pill" }}
                      createOrder={() => {
                        return createPayPalOrder(selectedPackage.package_id);
                      }}
                      onError={(err) => {
                        console.error('PayPal error:', err);
                        toast.error('Payment failed. Please try again.');
                      }}
                    />
                  </PayPalScriptProvider>
                </div>

                {/* v3.0.140 — Stripe Checkout (card, Apple Pay, Google Pay).
                    Redirects to Stripe-hosted page; comes back to
                    /payment/success or /payment/cancel. */}
                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-xs text-zinc-500 uppercase tracking-wider">or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                <Button
                  onClick={async () => {
                    try {
                      const { data } = await apiFetch('/stripe/checkout/create', {
                        method: 'POST',
                        body: JSON.stringify({
                          package_id: selectedPackage.package_id,
                          origin_url: window.location.origin,
                        }),
                      });
                      if (data?.checkout_url) {
                        window.location.href = data.checkout_url;
                      } else {
                        toast.error('Could not start Stripe checkout');
                      }
                    } catch (err) {
                      toast.error(`Stripe error: ${err.message || err}`);
                    }
                  }}
                  className="w-full h-11 bg-[#635BFF] hover:bg-[#524adb] text-white font-semibold rounded-full"
                  data-testid="stripe-checkout-btn"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay with Card (Stripe)
                </Button>

                <button 
                  onClick={() => setSelectedPackage(null)}
                  className="w-full text-center text-zinc-400 hover:text-white text-sm"
                >
                  ← Choose different package
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete {deleteDialog.type === 'song' ? 'Song' : 'Video'}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <span className="block mb-2">
                Are you sure you want to delete "<span className="text-white font-medium">{deleteDialog.title}</span>"?
              </span>
              {deleteDialog.type === 'song' ? (
                <span className="block text-emerald-400/90">
                  It'll be moved to your <a href="/trash" className="underline hover:text-emerald-300">Recently deleted</a> bin and kept for 30 days — you can restore it anytime.
                </span>
              ) : (
                <span className="block text-red-400 font-medium">
                  This action is permanent and cannot be undone.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className={deleteDialog.type === 'song'
                ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                : 'bg-red-600 text-white hover:bg-red-700'
              }
              data-testid="confirm-delete-btn"
            >
              {deleteDialog.type === 'song' ? 'Move to trash' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Community Coming Soon Modal */}
      {showCommunityModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowCommunityModal(false)}>
          <div 
            className="bg-zinc-900 border border-zinc-700 rounded-2xl p-8 max-w-md w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-5xl mb-4">💬</div>
            <h2 className="text-2xl font-bold text-white mb-3">Community is coming!</h2>
            <p className="text-zinc-400 mb-6">
              Share songs, get feedback, and connect with other creators.
            </p>
            <p className="text-white font-medium mb-4">Want early access?</p>
            {alreadyOnWaitlist ? (
              <div className="bg-green-500/20 text-green-400 px-4 py-3 rounded-xl font-medium">
                ✓ You're on the waitlist!
              </div>
            ) : (
              <Button
                onClick={handleJoinWaitlist}
                disabled={joiningWaitlist}
                className="btn-primary px-8 py-3"
              >
                {joiningWaitlist ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Notify Me'
                )}
              </Button>
            )}
            <button 
              onClick={() => setShowCommunityModal(false)}
              className="block w-full mt-4 text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Inline Video Popup Player — opens on Dashboard so closing returns the
          user to their exact scroll/tab state without a /dashboard reload. */}
      {popupVideo && (() => {
        const playable = videos.filter(v => v.status === 'completed' && v.video_url);
        const idx = playable.findIndex(v => v.video_id === popupVideo.video_id);
        const hasPrevNext = playable.length > 1;
        const goPrev = () => setPopupVideo(playable[(idx - 1 + playable.length) % playable.length]);
        const goNext = () => setPopupVideo(playable[(idx + 1) % playable.length]);
        return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closePopupVideo(); }}
          data-testid="dashboard-video-popup-overlay"
        >
          <div className="relative w-full max-w-4xl">
            {/* Standard fullscreen-player exit (top-right, pill, visible on bright content) */}
            <button
              onClick={closePopupVideo}
              aria-label="Close player"
              title="Close (Esc)"
              data-testid="dashboard-video-popup-close-btn"
              className="absolute -top-3 -right-3 z-[60] p-2 rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Prev / Next chevrons (only shown when 2+ playable videos) */}
            {hasPrevNext && (
              <>
                <button
                  onClick={goPrev}
                  aria-label="Previous video"
                  title="Previous (←)"
                  data-testid="dashboard-video-popup-prev-btn"
                  className="hidden sm:flex absolute -left-14 top-1/2 -translate-y-1/2 z-[60] h-10 w-10 items-center justify-center rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={goNext}
                  aria-label="Next video"
                  title="Next (→)"
                  data-testid="dashboard-video-popup-next-btn"
                  className="hidden sm:flex absolute -right-14 top-1/2 -translate-y-1/2 z-[60] h-10 w-10 items-center justify-center rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}

            <div className="bg-black rounded-xl overflow-hidden shadow-2xl border border-white/10">
              <video
                ref={popupVideoRef}
                key={popupVideo.video_id}
                src={popupVideo.video_url?.startsWith('http')
                  ? popupVideo.video_url
                  : `${process.env.REACT_APP_BACKEND_URL}${popupVideo.video_url}`}
                controls
                autoPlay
                playsInline
                className="w-full max-h-[80vh] bg-black"
                data-testid="dashboard-video-popup-player"
              />
              <div className="px-4 py-3 flex items-center justify-between gap-3 bg-zinc-900/80">
                <h3 className="text-white font-semibold truncate text-sm">
                  {popupVideo.title || 'Video'}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const id = popupVideo.video_id;
                    closePopupVideo();
                    navigate(`/video/${id}`);
                  }}
                  className="text-zinc-300 hover:text-white shrink-0"
                  data-testid="dashboard-video-popup-open-detail"
                >
                  Open detail
                </Button>
              </div>
            </div>

            {/* Keyboard-shortcut hint */}
            <div
              className="mt-3 text-center text-[11px] text-white/40 select-none pointer-events-none"
              data-testid="dashboard-video-popup-keyhint"
            >
              {hasPrevNext ? (
                <>
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10 font-mono">←</kbd>
                  {' '}<kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10 font-mono">→</kbd>
                  {' '}to navigate · <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10 font-mono">Esc</kbd> to close
                </>
              ) : (
                <>
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10 font-mono">Esc</kbd>
                  {' '}to close
                </>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Inline Song Popup Player — same pattern as the video popup. Lets users
          quick-play a song without leaving /dashboard. The "Open detail" button
          is the explicit escape hatch for users who want the full song page. */}
      {popupSong && (() => {
        const playable = songs.filter(s => s.status === 'completed' && s.audio_url);
        const idx = playable.findIndex(s => s.song_id === popupSong.song_id);
        const hasPrevNext = playable.length > 1;
        const goPrev = () => setPopupSong(playable[(idx - 1 + playable.length) % playable.length]);
        const goNext = () => setPopupSong(playable[(idx + 1) % playable.length]);
        return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closePopupSong(); }}
          data-testid="dashboard-song-popup-overlay"
        >
          <div className="relative w-full max-w-md">
            {/* Standard fullscreen-player exit */}
            <button
              onClick={closePopupSong}
              aria-label="Close player"
              title="Close (Esc)"
              data-testid="dashboard-song-popup-close-btn"
              className="absolute -top-3 -right-3 z-[60] p-2 rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Prev / Next chevrons (only shown when 2+ playable songs) */}
            {hasPrevNext && (
              <>
                <button
                  onClick={goPrev}
                  aria-label="Previous song"
                  title="Previous (←)"
                  data-testid="dashboard-song-popup-prev-btn"
                  className="hidden sm:flex absolute -left-14 top-1/2 -translate-y-1/2 z-[60] h-10 w-10 items-center justify-center rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={goNext}
                  aria-label="Next song"
                  title="Next (→)"
                  data-testid="dashboard-song-popup-next-btn"
                  className="hidden sm:flex absolute -right-14 top-1/2 -translate-y-1/2 z-[60] h-10 w-10 items-center justify-center rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}

            <div className="bg-zinc-900 rounded-xl overflow-hidden shadow-2xl border border-white/10">
              {/* Cover */}
              <div className="aspect-square bg-gradient-to-br from-purple-900/50 to-zinc-900">
                {popupSong.image_url ? (
                  <img
                    src={popupSong.image_url}
                    alt={popupSong.title || 'Song cover'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Music className="h-16 w-16 text-white/20" />
                  </div>
                )}
              </div>

              {/* Title + audio + actions */}
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold truncate">
                      {popupSong.title || 'Untitled'}
                    </h3>
                    {popupSong.tags && (
                      <p className="text-zinc-400 text-xs truncate mt-0.5">{popupSong.tags}</p>
                    )}
                  </div>
                  {popupSong.locked && (
                    <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-[10px] font-bold flex items-center gap-1 shrink-0">
                      <Lock className="h-3 w-3" /> Locked
                    </span>
                  )}
                </div>

                <audio
                  ref={popupAudioRef}
                  key={popupSong.song_id}
                  src={popupSong.audio_url}
                  controls
                  autoPlay
                  className="w-full"
                  data-testid="dashboard-song-popup-player"
                />

                <div className="flex items-center justify-between gap-2 pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const id = popupSong.song_id;
                      closePopupSong();
                      navigate(`/song/${id}`);
                    }}
                    className="text-zinc-300 hover:text-white"
                    data-testid="dashboard-song-popup-open-detail"
                  >
                    Open detail
                  </Button>
                  {!popupSong.locked && (
                    <ShareButtons
                      type="song"
                      id={popupSong.song_id}
                      title={popupSong.title}
                      compact
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Keyboard-shortcut hint */}
            <div
              className="mt-3 text-center text-[11px] text-white/40 select-none pointer-events-none"
              data-testid="dashboard-song-popup-keyhint"
            >
              {hasPrevNext ? (
                <>
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10 font-mono">←</kbd>
                  {' '}<kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10 font-mono">→</kbd>
                  {' '}to navigate · <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10 font-mono">Esc</kbd> to close
                </>
              ) : (
                <>
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/10 font-mono">Esc</kbd>
                  {' '}to close
                </>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* v3.0.134 — Floating action bar for multi-select "Add to Playlist".
          v3.0.136 — When arriving via ?addTo=<id>, add DIRECTLY to that
          playlist (no dialog) and return to /playlist/<id> after. */}
      {selectMode && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur border-t border-white/10 px-4 py-3 z-40"
          data-testid="select-mode-actionbar"
        >
          <div className="max-w-6xl mx-auto flex items-center gap-3 flex-wrap">
            <div className="text-sm text-white">
              <span className="font-semibold text-emerald-300" data-testid="select-count">
                {selectedSongIds.length}
              </span>{' '}
              song{selectedSongIds.length === 1 ? '' : 's'} selected
              {addToPlaylistId && addToPlaylist && (
                <span className="hidden sm:inline text-zinc-500">
                  {' '}→ "<span className="text-emerald-300">{addToPlaylist.name}</span>"
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={selectedSongIds.length === 0}
              onClick={() => setSelectedSongIds([])}
              className="text-zinc-400 hover:text-white hover:bg-white/10"
              data-testid="select-clear-btn"
            >
              Clear
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const eligible = filteredSongs
                  .filter((s) => s.status === 'completed')
                  .map((s) => s.song_id);
                setSelectedSongIds(eligible);
              }}
              className="text-zinc-400 hover:text-white hover:bg-white/10"
              data-testid="select-all-btn"
            >
              Select all completed
            </Button>
            <div className="flex-1" />
            {addToPlaylistId ? (
              <Button
                onClick={bulkAddToTargetPlaylist}
                disabled={selectedSongIds.length === 0 || addingBulk}
                className="bg-emerald-500 text-black hover:bg-emerald-600"
                data-testid="add-to-target-playlist-btn"
              >
                {addingBulk ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <ListMusic className="h-4 w-4 mr-1.5" />
                )}
                Add {selectedSongIds.length || ''} song{selectedSongIds.length === 1 ? '' : 's'}
              </Button>
            ) : (
              <Button
                onClick={() => setShowPlaylistDialog(true)}
                disabled={selectedSongIds.length === 0}
                className="bg-emerald-500 text-black hover:bg-emerald-600"
                data-testid="bulk-add-to-playlist-btn"
              >
                <ListMusic className="h-4 w-4 mr-1.5" />
                Add to Playlist
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                if (addToPlaylistId) {
                  navigate(`/playlist/${addToPlaylistId}`);
                } else {
                  exitSelectMode();
                }
              }}
              className="border-white/20 text-zinc-300 hover:bg-white/10"
              data-testid="select-mode-exit-btn"
            >
              <X className="h-4 w-4 mr-1.5" />
              {addToPlaylistId ? 'Cancel' : 'Done'}
            </Button>
          </div>
        </div>
      )}

      {/* v3.0.134 — Add to Playlist dialog */}
      <AddToPlaylistDialog
        open={showPlaylistDialog}
        onOpenChange={(v) => {
          setShowPlaylistDialog(v);
          if (!v) {
            // Give user a moment to see success, then exit select mode
            setTimeout(() => exitSelectMode(), 200);
          }
        }}
        songIds={selectedSongIds}
      />

      {/* v3.0.141 — Manage Categories modal */}
      <ManageCategoriesModal
        open={showManageCategories}
        onOpenChange={setShowManageCategories}
        onChange={bumpCategoriesRefresh}
      />
    </div>
  );
}
